import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { base44 } from "@/api/base44Client";
import { ArrowLeft, ArrowRight, FileDown } from "lucide-react";
import ProductCard from "@/components/products/ProductCard";
import { pushEvent } from "@/lib/gtm";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState("");
  const [imageMeta, setImageMeta] = useState({}); // { url: { alt_text, title } }

  useEffect(() => {
    setLoading(true);
    base44.entities.Product.list("-updated_date", 500)
      .then(async (list) => {
        const p = list.find((item) => item.slug === id || item.id === id);
        if (!p) {
          setProduct(null);
          return;
        }
        setProduct(p);
        setActiveImage(p.image);

        // Load image title/alt from the uploads catalog (FileItem)
        const files = await base44.entities.FileItem.filter({ fileType: "image" }, "-updated_date", 1000).catch(() => []);
        const metaMap = {};
        files.forEach((f) => { metaMap[f.url] = { alt_text: f.alt_text, title: f.title }; });
        setImageMeta(metaMap);

        // Dynamic SEO per product page
        document.title = p.seo_title || `${p.name} | Seisfra Philippines`;
        const setMeta = (attr, key, content) => {
          if (!content) return;
          let el = document.head.querySelector(`meta[${attr}="${key}"]`);
          if (!el) {
            el = document.createElement("meta");
            el.setAttribute(attr, key);
            document.head.appendChild(el);
          }
          el.setAttribute("content", content);
        };
        const desc = p.meta_description || (p.description || "").replace(/<[^>]*>/g, "").slice(0, 160);
        setMeta("name", "description", desc);
        setMeta("property", "og:title", p.seo_title || p.name);
        setMeta("property", "og:description", desc);
        setMeta("property", "og:image", p.image);
        const rel = list.filter((r) => r.category === p.category && r.status === "active" && r.id !== p.id);
        setRelated(rel.slice(0, 3));
      })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-gray-200 border-t-[#E4001C] rounded-full animate-spin" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 px-6 text-center">
        <h1 className="font-heading font-extrabold uppercase text-4xl">Product Not Found</h1>
        <Link to="/products" className="inline-flex items-center gap-2 bg-[#E4001C] text-white font-tech font-semibold uppercase tracking-widest px-6 py-3">
          <ArrowLeft className="w-4 h-4" /> Back to Products
        </Link>
      </div>
    );
  }

  return (
    <>
      <section className="bg-[#0A0A0A] pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <Link to="/products" className="inline-flex items-center gap-2 font-tech uppercase tracking-widest text-sm text-[#A8ADB4] hover:text-white transition-colors mb-6">
            <ArrowLeft className="w-4 h-4" /> All Products
          </Link>
          <p className="font-tech uppercase tracking-[0.2em] text-[#FF4D5E] text-sm mb-2">{product.category}</p>
          <h1 className="font-heading font-extrabold uppercase text-white text-4xl md:text-6xl tracking-wide">{product.name}</h1>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <div className="relative">
              <div className="absolute -top-3 -left-3 w-full h-full border-2 border-[#E4001C]" />
              <img src={activeImage || product.image}
                alt={imageMeta[activeImage || product.image]?.alt_text || product.name}
                title={imageMeta[activeImage || product.image]?.title || product.name}
                className="relative w-full aspect-[4/3] object-cover" />
            </div>
            {(product.gallery || []).length > 0 && (
              <div className="flex gap-3 mt-6">
                {[product.image, ...product.gallery].filter(Boolean).map((url, i) => (
                  <button key={i} type="button"
                    onClick={() => {
                      setActiveImage(url);
                      pushEvent("product_gallery_click", {
                        product_name: product.name,
                        image_index: i,
                        image_name: imageMeta[url]?.title || imageMeta[url]?.alt_text || `${product.name} image ${i + 1}`,
                        image_url: url,
                      });
                    }}
                    className={`w-20 h-20 border-2 overflow-hidden transition-colors ${(activeImage || product.image) === url ? "border-[#E4001C]" : "border-gray-200 hover:border-gray-400"}`}>
                    <img src={url} alt={`${product.name} ${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <div className="text-[#171717]/70 leading-relaxed mb-8 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: product.description || "" }} />
            <h2 className="font-heading font-extrabold uppercase text-2xl tracking-wide mb-4 text-[#171717]">Specifications</h2>
            <table className="w-full text-sm mb-8">
              <tbody>
                {(product.specs || []).map((s, i) => (
                  <tr key={i} className={i % 2 ? "bg-white" : "bg-[#F5F5F5]"}>
                    <td className="py-3 px-4 font-tech font-semibold uppercase tracking-wide text-[#171717] w-1/2">{s.label}</td>
                    <td className="py-3 px-4 text-[#171717]/70">{s.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex flex-wrap gap-4">
              <Link to={`/contact?product=${encodeURIComponent(product.name)}`}
                onClick={() => pushEvent("request_quote_click", { location: "product_detail", product_name: product.name })}
                className="inline-flex items-center gap-2 bg-[#E4001C] hover:bg-[#c00018] text-white font-tech font-semibold uppercase tracking-widest px-8 py-4 transition-all hover:gap-4">
                Request Quote <ArrowRight className="w-5 h-5" />
              </Link>
              <Link to={`/contact?product=${encodeURIComponent(product.name)}`}
                onClick={() => pushEvent("request_spec_sheet_click", { location: "product_detail", product_name: product.name })}
                className="inline-flex items-center gap-2 border border-[#0A0A0A] hover:border-[#E4001C] hover:text-[#E4001C] font-tech font-semibold uppercase tracking-widest px-8 py-4 transition-colors">
                <FileDown className="w-5 h-5" /> Request Spec Sheet
              </Link>
            </div>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="bg-[#F5F5F5] py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <h2 className="font-heading font-extrabold uppercase text-3xl tracking-wide mb-10 text-[#171717]">Related Systems</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {related.map((p) => (
                <div key={p.id}
                  onClickCapture={() => pushEvent("related_product_click", { product_name: p.name, category: p.category, from_product: product.name })}>
                  <ProductCard product={p} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}